import AppFadeView from "@toothfairy/shared-ui/AppFadeView";
import AppForms from "@toothfairy/shared-ui/AppForms";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppMenu from "@toothfairy/shared-ui/AppMenu";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppUser from "@toothfairy/shared-ui/AppUser";
import React, { useCallback, useMemo } from "react";
import { View } from "react-native";
import entitiesHelper from "../../../API/EntitiesAI/entitiesHelper";
import ToothFairyButton from "../../../Components/ToothFairyComponents/ToothFairyButton";
import Constants from "../../../Constants";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import useWorkspaces from "../../../Hooks/useWorkspaces";
import IdCopyView from "../../../Components/ToothFairyComponents/IdCopyView";
import ColorPickerField from "../../../Components/ToothFairyComponents/ColorPickerField";
import EmojiPickerField from "../../../Components/ToothFairyComponents/EmojiPickerField";
import AppToast from "@toothfairy/shared-ui/AppToast";
import { useTranslation } from "react-i18next";
import Utility from "../../../Utility";
import { isLargeScreen } from "../../MainViewContainer";
import Agents from "../../../API/Agents";
import sitesHelper from "../../../API/Sites/sitesHelper";
import { Agent, Site } from "../../../models";

const SettingsAI = ({
  sectionTitle,
  initialValues = { label: "" },
  settingType,
  isEdit,
  opt,
  setDisplayedView,
  backTo,
  onEntityCreated,
}) => {
  const generateName = () => {
    switch (settingType) {
      case "intent":
        return "Intent";
      case "ner":
        return "NER";
      case "topic":
        return "Topic";
      default:
        return "";
    }
  };
  const AppToasty = AppToast.useToast();
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const entityName = generateName();
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const {
    getWorkspaceBaseModel,
    getPlanConfig,
    getActiveWorkspace,
    loadAgents,
  } = useWorkspaces();
  const { user } = AppUser.useUser();
  const validationSchema = AppForms.yup.object().shape({
    label: AppForms.yup.string().required().label(`${entityName} name`),
    backgroundColor: AppForms.yup.string().nullable().required(),
    emoji: AppForms.yup.string().nullable(),
    parentEntity: AppForms.yup.string().nullable(),
  });
  const _title = useCallback(() => {
    return isEdit ? `Save` : `Create`;
  }, [entityName, isEdit]);
  const handleDeletion = async () => {
    try {
      const result = await entitiesHelper.deleteEntity(opt.id);
      for (let i = 0; i < state?.agents?.length; i++) {
        const _topics =
          typeof state?.agents[i]?.allowedTopics === "string"
            ? JSON.parse(state?.agents[i]?.allowedTopics)
            : state?.agents[i]?.allowedTopics;
        if (_topics?.map((u) => u?.id)?.includes(opt.id)) {
          const newTopics = _topics?.filter((u) => u?.id !== opt.id);
          const newAgent = await Agents.updateAgent(state?.agents[i]?.id, [
            {
              property: "allowedTopics",
              value: JSON.stringify(newTopics),
            },
          ], user?.id);
          setTimeout(() => {}, 500);
        }
      }
      let resultAgents = await loadAgents(getActiveWorkspace()?.id);
      for (let i = 0; i < state?.sites?.length; i++) {
        const _topics =
          typeof state?.sites[i]?.topics === "string"
            ? JSON.parse(state?.sites[i]?.topics)
            : state?.sites[i]?.topics;
        if (_topics?.includes(opt.id)) {
          const newTopics = _topics?.filter((u) => u !== opt.id);
          const newSite = await sitesHelper.updateSite(state?.sites[i]?.id, [
            {
              property: "topics",
              value: JSON.stringify(newTopics),
            },
          ]);
          setTimeout(() => {}, 500);
        }
      }
      let resultSites = await sitesHelper.getSitesByWorkspaceID(
        getActiveWorkspace()?.id
      );
      resultSites = resultSites?.data?.sitesByWorkspace;
      dispatch("SET_SITES", resultSites);
      state.aiEntitiesConfig.splice(identifyOptByID(opt.id), 1);

      dispatch("SET_AI_ENTITIES_CONFIG", state.aiEntitiesConfig);
      navigateBack();
      AppToasty.show(
        `${Utility.upperCaseFirstLetter(opt?.type)} ${t("deleted")}`,
        {
          type: "success",
        }
      );
    } catch (error) {
      console.error("entityDeleteErrorCaption", error);
      AppToasty.show(
        `${t("entityDeleteErrorCaption") + " "} ${Utility.upperCaseFirstLetter(
          opt?.type
        )}`,
        {
          type: "danger",
        }
      );
    }
  };
  const navigateBack = () => {
    setDisplayedView(backTo);
  };
  const identifyOptByID = (id) => {
    return state.aiEntitiesConfig.map((u) => u.id).indexOf(id);
  };
  const handleEntitySave = async (v) => {
    try {
      let result;
      const payload = [
        {
          property: "label",
          value: v.label,
        },
        {
          property: "emoji",
          value: v.emoji || "",
        },
        {
          property: "backgroundColor",
          value: v.backgroundColor || "#fff",
        },
        {
          property: "parentEntity",
          value: v.parentEntity,
        },
        {
          property: "description",
          value: v.description || "",
        },
      ];
      if (isEdit) {
        result = await entitiesHelper.updateEntity(opt.id, payload);
        const updatedEntities = [...state.aiEntitiesConfig];
        updatedEntities[identifyOptByID(result.id)] = result;
        dispatch("SET_AI_ENTITIES_CONFIG", updatedEntities);
      } else {
        result = await entitiesHelper.createEntity({
          label: v.label,
          emoji: v?.emoji,
          backgroundColor: v?.backgroundColor,
          description: v?.description,
          parentEntity: v.parentEntity,
          type: settingType,
          workspaceID: state.activeWorkspaceID,
        });
        dispatch("SET_AI_ENTITIES_CONFIG", [...state.aiEntitiesConfig, result]);
      }
      backTo && navigateBack();
      onEntityCreated && onEntityCreated("success");
    } catch (error) {
      console.error(error);
      onEntityCreated && onEntityCreated("error");
    }
  };

  const _view = useMemo(
    () => (
      <AppFadeView
        style={{
          horizontalMargin: 10,
          paddingHorizontal: 20,
          minWidth: isLargeScreen() ? 450 : 300,
          paddingBottom: 10,
          // height: "90%",
          overflow: "auto",
        }}
      >
        {backTo && (
          <AppMenu.DefaultBackOptionComponent
            navigateBack={navigateBack}
            sectionTitle={generateName()?.toUpperCase() + ": " + sectionTitle}
            isClickable
            isBackIconDisplayed
          />
        )}
        {isEdit && <IdCopyView id={opt?.id} />}

        <AppForms.AppForm
          key={opt?.id}
          initialValues={initialValues}
          onSubmit={handleEntitySave}
          validationSchema={validationSchema}
          enableReinitialize
        >
          {
            <>
              <AppForms.AppFormField
                showTextInputLabel
                isMandatory
                wrapperInputStyle={{
                  paddingTop: 10,
                }}
                label={t("settings.list.name")}
                placeholder={t("settings.list.name")}
                autoCapitalise
                keyboardtype="default"
                textContextType="label"
                autoCorrect={false}
                name="label"
              />
              <AppForms.AppFormField
                showTextInputLabel
                wrapperInputStyle={{
                  paddingTop: 10,
                }}
                label={t("settings.list.description")}
                placeholder={
                  settingType == "topic"
                    ? t("settings.list.topicDescriptionPlaceholder")
                    : t("settings.list.description")
                }
                multiline={true}
                textContainer={{
                  minHeight: 120,
                  paddingVertical: 10,
                  marginHorizontal: 5,
                }}
                keyboardtype="default"
                textContextType="label"
                autoCorrect={false}
                name="description"
              />
              <ColorPickerField
                showTextInputLabel
                errorOffset={-13}
                wrapperInputStyle={{
                  paddingVertical: 10,
                }}
                label={t("settings.list.color")}
                placeholder={t("settings.list.hexColorPlaceholder", {
                  defaultValue: "#5d94d6",
                })}
                name="backgroundColor"
                previewColor={currentTheme?.theme?.primary}
              />
              {settingType === "intent" &&
                state.workspaces.filter(
                  (w) => w.id === state.activeWorkspaceID
                )[0].type === "hierarchical" && (
                  <AppForms.AppFormPicker
                    showTextInputLabel
                    label={t("settings.list.setParentIntent")}
                    name={"parentEntity"}
                    numberOfColumns={1}
                    optionKey="id"
                    optionLabel="name"
                    options={{ id: "dd", name: "My parent intent" }}
                    placeholder={t("settings.list.noParentSet")}
                    textStyle={{
                      fontSize: currentTheme?.theme?.small_font_size,
                    }}
                    title={t("settings.list.chooseParentIntent")}
                    modalBackdrop={currentTheme?.theme?.softBackgroundColor}
                  />
                )}
              {(settingType === "ner" || settingType === "topic") && (
                <EmojiPickerField
                  showTextInputLabel
                  errorOffset={-13}
                  wrapperInputStyle={{
                    paddingVertical: 10,
                  }}
                  label={t("settings.list.icon")}
                  placeholder={t("settings.list.icon")}
                  name="emoji"
                />
              )}
            </>
          }
          <View style={{ alignItems: "center" }}>
            <AppForms.AppSubmitButton
              title={_title()}
              awesomeIconColor={currentTheme?.theme?.white}
              backgroundColor={currentTheme?.theme?.primary}
              btnContentStyle={{
                paddingLeft: 0,
                paddingRight: 0,
              }}
              style={[
                {
                  height: Constants.appButtonHeight,
                  width: "auto",
                  shadowColor: currentTheme?.theme?.transparent,
                  marginHorizontal: 10,
                },
              ]}
              // btnLabelStyle={{ fontSize: 16 }}
              wrapperStyle={[
                {
                  width: "fit-content",
                  minWidth: 150,
                  marginTop: 20,
                  height: Constants.appButtonHeight,
                },
              ]}
              // backgroundColor={currentTheme?.theme?.green}

              // awesomeIcon={AppAssets.icons.add_icon}
              // awesomeIconColor={currentTheme?.theme?.white}
            />
            {isEdit && (
              <ToothFairyButton
                title={t("settings.list.delete")}
                onPress={handleDeletion}
                isDeletion
                isDisabled={
                  settingType === "intent" && getWorkspaceBaseModel() !== "none"
                }
                wrapperStyle={{
                  marginTop: 30,
                  minWidth: 150,
                  height: Constants.appButtonHeight,
                }}
              />
            )}
          </View>
        </AppForms.AppForm>
      </AppFadeView>
    ),
    [
      state.activeWorkspaceID,
      initialValues,
      state?.sites?.length,
      state?.agents?.length,
    ]
  );

  return _view;
};

export default SettingsAI;
